import { useState, useEffect } from 'react';
import {
  PlannedWorkout,
  useProfile,
  useRacePlan,
  usePlannedWorkouts,
  useWorkouts,
} from '../hooks/useProfile';
import { useUnacknowledgedWorkouts } from '../hooks/useUnacknowledgedWorkouts';
import TodaysWorkouts from '../components/TodaysWorkouts';
import { WorkoutWeekHeader } from '../components/WorkoutWeekHeader';
import WeeklyMetrics from '../components/WeeklyMetrics';
import WorkoutModal from '../components/workout/WorkoutModal';
import { dayjs } from '../lib/utils';
import WorkoutCompletionModal from '../components/Modals/WorkoutCompletionModal';
import { useNavigate } from '@tanstack/react-router';
import { CalendarPlus, Plus, Radio, Target } from 'lucide-react';
import { ConnectionPrompt } from '../components/ConnectionPrompt';
import PageHeader from '../components/PageHeader';
import TrainingHero from '../components/TrainingHero';

const Dashboard = () => {
  const [selectedWorkout, setSelectedWorkout] = useState<PlannedWorkout | null>(
    null,
  );
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [selectedWorkoutDate, setSelectedWorkoutDate] = useState(dayjs());
  const { data: unacknowledgedData } = useUnacknowledgedWorkouts();
  const { data: profile, isLoading: isProfileLoading } = useProfile();
  const { data: racePlan, isLoading: isRacePlanLoading } = useRacePlan();
  const { data: plannedWorkouts, isLoading: isPlannedWorkoutsLoading } =
    usePlannedWorkouts({
      startDate: dayjs().subtract(90, 'day').toDate(),
    });
  const { data: completedWorkoutData, isLoading: isWorkoutsLoading } =
    useWorkouts();
  const navigate = useNavigate();

  const hasWorkoutConnection = Boolean(
    profile?.user?.connectionId ||
    profile?.user?.connections?.some(
      (connection: { connectionType: string }) =>
        connection.connectionType === 'strava' ||
        connection.connectionType === 'garmin',
    ),
  );
  const planWorkouts = plannedWorkouts || [];
  const linkedWorkoutIds = new Set(
    planWorkouts
      .map((workout) => workout.activity_id)
      .filter((activityId) => activityId != null)
      .map(String),
  );
  const hasUnlinkedWorkoutOnSelectedDate = Boolean(
    completedWorkoutData?.workouts.some(
      (workout) =>
        dayjs(workout.completedAt).isSame(selectedWorkoutDate, 'day') &&
        !linkedWorkoutIds.has(String(workout.id)),
    ),
  );
  const isDashboardLoading =
    isProfileLoading ||
    isRacePlanLoading ||
    isPlannedWorkoutsLoading ||
    isWorkoutsLoading;
  const dashboardState = racePlan
    ? 'active'
    : hasWorkoutConnection
      ? 'connected'
      : 'new';

  // Show completion modal when there are unacknowledged workouts
  useEffect(() => {
    if (unacknowledgedData && unacknowledgedData.workouts.length > 0) {
      setShowCompletionModal(true);
    }
  }, [unacknowledgedData]);

  // Listen for header "+" button to open the log workout page
  useEffect(() => {
    const handler = () => navigate({ to: '/workouts/log' });
    window.addEventListener('openLogWorkoutModal', handler);
    return () => window.removeEventListener('openLogWorkoutModal', handler);
  }, [navigate]);

  return (
    <div className="mx-auto w-full max-w-5xl px-1 md:px-0">
      <PageHeader
        eyebrow="Your training cockpit"
        title={`Good morning${profile?.user?.name ? `, ${profile.user.name.split(' ')[0]}` : ''}`}
        description="Keep today simple: see what is next, log what you did, and build momentum."
         action={
           <button
             type="button"
             className={dashboardState === 'active' ? 'button-secondary' : 'button-primary'}
             onClick={() => navigate({ to: dashboardState === 'new' ? '/races' : '/workouts/log' })}
           >
             {dashboardState === 'new' ? <Target className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
             {dashboardState === 'new' ? 'Choose a goal race' : 'Log workout'}
           </button>
         }
      />
      <div className="space-y-5 md:space-y-6">
        {racePlan ? (
          <TrainingHero racePlan={racePlan} plannedWorkouts={planWorkouts} />
        ) : null}
        {!isPlannedWorkoutsLoading && (racePlan || planWorkouts.length > 0) ? (
          <WorkoutWeekHeader
            selectedDate={selectedWorkoutDate}
            plannedWorkouts={planWorkouts}
            onSelectDate={setSelectedWorkoutDate}
          />
        ) : null}

         {!isProfileLoading && !hasWorkoutConnection ? (
           <div className="surface-card rounded-2xl border-brand-cobalt/20 p-5 sm:p-6">
             <div className="flex items-start gap-4">
               <div className="rounded-xl bg-brand-blueSoft p-3 text-brand-cobalt"><Radio className="h-5 w-5" /></div>
               <div>
                 <p className="eyebrow">Optional, but useful</p>
                 <h2 className="mt-1 text-xl font-bold text-brand-midnight dark:text-white">Connect your activity data</h2>
                 <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-slate-300">Sync completed sessions automatically so your plan can reflect the work you have done.</p>
                 <ConnectionPrompt />
               </div>
             </div>
           </div>
         ) : null}

        {!isDashboardLoading && !racePlan && planWorkouts.length === 0 ? (
          <div className="surface-card rounded-2xl p-6 sm:p-8">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-brand-volt to-brand-voltSoft shadow-[0_10px_24px_-12px_rgba(196,255,0,0.9)] ring-1 ring-brand-midnight/10">
              <CalendarPlus className="h-6 w-6 text-brand-midnight" />
            </div>
            <p className="mt-5 text-xs font-bold uppercase tracking-[0.16em] text-brand-cobalt">
              Your training starts here
            </p>
            <h2 className="mt-2 text-2xl font-black tracking-[-0.03em] text-brand-midnight">
              Build your personalized plan
            </h2>
            <p className="mt-2 max-w-xl text-sm leading-6 text-gray-600 sm:text-base">
              Tell us about your race and goals, and we’ll create a training
              plan that fits your schedule.
            </p>
            <button
              type="button"
              onClick={() =>
                navigate({
                  to: profile?.user?.isPaidSubscriber
                    ? '/plan/create'
                    : '/checkout',
                })
              }
              className="button-primary mt-6 min-h-11 w-full px-5 py-3 sm:w-auto"
            >
              {profile?.user?.isPaidSubscriber
                ? 'Create my race plan'
                : 'Unlock my race plan'}{' '}
              <span className="text-brand-volt">→</span>
            </button>
             <button
               type="button"
               onClick={() => navigate({ to: profile?.user?.isPaidSubscriber ? '/plan/workout/new' : '/checkout' })}
              className="mt-3 min-h-11 w-full rounded-full border border-gray-200 bg-white px-5 py-3 text-sm font-semibold text-brand-midnight transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-transparent dark:text-white dark:hover:bg-gray-900 sm:w-auto"
            >
               {profile?.user?.isPaidSubscriber
                 ? 'Or generate a single workout →'
                 : 'Unlock single workouts with membership →'}
            </button>
          </div>
        ) : null}

        <div className="space-y-6">
          {/* Compact CTA to generate a full race plan when user has workouts
            but no race plan yet. */}
          {!isDashboardLoading && !racePlan && planWorkouts.length > 0 ? (
            <div className="depth-panel flex flex-col gap-3 rounded-2xl p-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-sm font-bold tracking-tight text-brand-midnight dark:text-white">
                  Want a full race training plan?
                </h2>
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                  Create a personalized plan around your goal race.
                </p>
              </div>
              <button
                type="button"
                onClick={() =>
                  navigate({
                    to: profile?.user?.isPaidSubscriber
                      ? '/plan/create'
                      : '/checkout',
                  })
                }
                className="button-primary shrink-0 px-5 py-2.5 text-xs"
              >
                Create race plan →
              </button>
            </div>
          ) : null}

          {/* Today's Workouts */}
          {!isDashboardLoading &&
          (planWorkouts.length > 0 || hasUnlinkedWorkoutOnSelectedDate) ? (
            <TodaysWorkouts
              selectedDate={selectedWorkoutDate}
              onSelectDate={setSelectedWorkoutDate}
            />
          ) : null}

          {/* Weekly Progress */}
          {!isProfileLoading && hasWorkoutConnection ? <WeeklyMetrics /> : null}
        </div>
      </div>

      {/* Workout Details Modal */}
      {selectedWorkout ? (
        <WorkoutModal
          isOpen={!!selectedWorkout}
          activityId={selectedWorkout.id.toString()}
          onClose={() => setSelectedWorkout(null)}
          workoutData={selectedWorkout}
        />
      ) : null}

      {/* Workout Completion Modal */}
      {unacknowledgedData ? (
        <WorkoutCompletionModal
          workouts={unacknowledgedData.workouts}
          show={showCompletionModal}
          onClose={() => setShowCompletionModal(false)}
        />
      ) : null}
    </div>
  );
};

export default Dashboard;
